import React, { useState, useEffect } from 'react';
import { db, handleFirestoreError, OperationType } from '../../lib/firebase';
import { collection, onSnapshot, addDoc, updateDoc, doc, deleteDoc } from 'firebase/firestore';
import { Prop, InventoryItem } from '../../types';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Plus, Trash2, Edit2, Layout, UserCheck, ShieldCheck, Box } from 'lucide-react';
import { toast } from 'sonner';
import LCARSConfirmationModal from '../LCARSConfirmationModal';

export default function PropManager() {
  const [props, setProps] = useState<Prop[]>([]);
  const [inventory, setInventory] = useState<InventoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<Partial<Prop>>({
    name: '',
    description: '',
    staffNeeded: 2,
    setupStaffNeeded: 2,
    takedownStaffNeeded: 2,
    inventoryItemIds: []
  });

  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    description: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    description: '',
    onConfirm: () => {},
  });

  useEffect(() => {
    const unsubProps = onSnapshot(collection(db, 'props'), (snap) => {
      setProps(snap.docs.map(d => ({ id: d.id, ...d.data() } as Prop)));
      setLoading(false);
    }, (err) => {
      handleFirestoreError(err, OperationType.LIST, 'props');
    });

    const unsubInventory = onSnapshot(collection(db, 'inventory'), (snap) => {
      setInventory(snap.docs.map(d => ({ id: d.id, ...d.data() } as InventoryItem)));
    });

    return () => {
      unsubProps();
      unsubInventory();
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const dbData = Object.fromEntries(Object.entries(formData).filter(([k, v]) => v !== undefined && k !== 'id'));
      if (editingId) {
        await updateDoc(doc(db, 'props', editingId), dbData);
        toast.success('Prop configuration updated');
      } else {
        await addDoc(collection(db, 'props'), dbData);
        toast.success('Prop added to terminal');
      }
      setEditingId(null);
      setIsAdding(false);
      setFormData({ name: '', description: '', staffNeeded: 2, setupStaffNeeded: 2, takedownStaffNeeded: 2, inventoryItemIds: [] });
    } catch (err) {
      handleFirestoreError(err, OperationType.WRITE, 'props');
    }
  };

  const toggleInventory = (itemId: string) => {
    const current = formData.inventoryItemIds || [];
    if (current.includes(itemId)) {
      setFormData({ ...formData, inventoryItemIds: current.filter(id => id !== itemId) });
    } else {
      setFormData({ ...formData, inventoryItemIds: [...current, itemId] });
    }
  };

  const handleDelete = (id: string) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Decommission Prop',
      description: 'Remove this prop configuration? Existing scheduled shifts will remain unaffected but new ones cannot be generated.',
      onConfirm: async () => {
        try {
          await deleteDoc(doc(db, 'props', id));
          toast.info('Prop decommissioned');
        } catch (err) {
          handleFirestoreError(err, OperationType.DELETE, 'props');
        }
      }
    });
  };

  const handleCleanDuplicates = async () => {
    setConfirmDialog({
      isOpen: true,
      title: 'Purge Duplicate Scenes',
      description: 'This will find and delete all duplicate scenes containing "general" in the name, keeping only one of each.',
      onConfirm: async () => {
        try {
          const generalProps = props.filter(p => p.name && p.name.toLowerCase().includes('general'));
          const seen = new Set<string>();
          const toDelete: Prop[] = [];
          
          for (const p of generalProps) {
            const nameLower = p.name.toLowerCase().trim();
            if (seen.has(nameLower)) {
              toDelete.push(p);
            } else {
              seen.add(nameLower);
            }
          }
          
          if (toDelete.length === 0) {
            toast.info('No duplicates found.');
            return;
          }
          
          for (const p of toDelete) {
            await deleteDoc(doc(db, 'props', p.id!));
          }
          
          toast.success(`Purged ${toDelete.length} duplicate scenes.`);
        } catch (err) {
          handleFirestoreError(err, OperationType.DELETE, 'props');
        }
      }
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center bg-lcars-purple/20 p-4 rounded-r-full border-l-8 border-lcars-purple">
        <div>
          <h2 className="text-2xl font-bold text-lcars-purple uppercase">Global Scene Configuration</h2>
          <p className="text-xs text-[var(--foreground)]/40 font-bold tracking-widest uppercase">Tactical Setup & Staffing Requirements</p>
        </div>
        <div className="flex gap-2">
          <button 
            onClick={handleCleanDuplicates}
            className="lcars-pill bg-lcars-red text-black flex items-center gap-2 px-6 py-2 text-sm font-bold uppercase"
          >
            <Trash2 className="w-4 h-4" /> PURGE DUPES
          </button>
          <button 
            onClick={() => { setIsAdding(true); setEditingId(null); }}
            className="lcars-pill bg-lcars-blue text-black flex items-center gap-2 px-6 py-2 text-sm font-bold uppercase"
          >
            <Plus className="w-4 h-4" /> CONFIGURE SCENE
          </button>
        </div>
      </div>

      {(isAdding || editingId) && (
        <form onSubmit={handleSubmit} className="bg-white/5 border border-lcars-purple p-6 rounded-r-full space-y-6 animate-in slide-in-from-top duration-300">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-bold text-lcars-purple uppercase tracking-widest">
              {editingId ? 'Edit Scene Details' : 'Configure New Scene'}
            </h3>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => { setIsAdding(false); setEditingId(null); }}>Cancel</Button>
              <Button type="submit" className="bg-lcars-purple text-black hover:bg-white">{editingId ? 'Save Changes' : 'Initialize Scene'}</Button>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-1 lg:col-span-1">
              <label htmlFor="prop-name" className="text-[0.6rem] text-lcars-purple font-bold uppercase ml-4">Scene Name</label>
              <Input 
                id="prop-name"
                value={formData.name}
                onChange={e => setFormData({ ...formData, name: e.target.value })}
                required
                className="bg-[var(--background)] border-lcars-purple text-[var(--foreground)]"
                placeholder="e.g., Full Bridge Setup"
              />
            </div>
            <div className="space-y-1">
              <label htmlFor="prop-staff" className="text-[0.6rem] text-lcars-purple font-bold uppercase ml-4">Shift Staff Required</label>
              <Input 
                id="prop-staff"
                type="number"
                min="1"
                value={formData.staffNeeded}
                onChange={e => setFormData({ ...formData, staffNeeded: parseInt(e.target.value) || 0 })}
                required
                className="bg-[var(--background)] border-lcars-purple text-[var(--foreground)]"
              />
            </div>
            <div className="space-y-1">
              <label htmlFor="prop-setup" className="text-[0.6rem] text-lcars-purple font-bold uppercase ml-4">Setup Staff Required</label>
              <Input 
                id="prop-setup"
                type="number"
                min="0"
                value={formData.setupStaffNeeded !== undefined ? formData.setupStaffNeeded : 2}
                onChange={e => setFormData({ ...formData, setupStaffNeeded: parseInt(e.target.value) || 0 })}
                required
                className="bg-[var(--background)] border-lcars-purple text-[var(--foreground)]"
              />
            </div>
            <div className="space-y-1">
              <label htmlFor="prop-takedown" className="text-[0.6rem] text-lcars-purple font-bold uppercase ml-4">Takedown Staff Required</label>
              <Input 
                id="prop-takedown"
                type="number"
                min="0"
                value={formData.takedownStaffNeeded !== undefined ? formData.takedownStaffNeeded : 2}
                onChange={e => setFormData({ ...formData, takedownStaffNeeded: parseInt(e.target.value) || 0 })}
                required
                className="bg-[var(--background)] border-lcars-purple text-[var(--foreground)]"
              />
            </div>
            <div className="space-y-1 lg:col-span-4">
              <label htmlFor="prop-desc" className="text-[0.6rem] text-lcars-purple font-bold uppercase ml-4">Description</label>
              <Input 
                id="prop-desc"
                value={formData.description}
                onChange={e => setFormData({ ...formData, description: e.target.value })}
                className="bg-[var(--background)] border-lcars-purple text-[var(--foreground)]"
                placeholder="Details for volunteers"
              />
            </div>
          </div>

          <div className="space-y-2">
            <div className="text-[0.6rem] text-lcars-purple font-bold uppercase ml-4">Required Physical Assets (Auto-Conflict Mitigation)</div>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2 bg-[var(--background)]/40 p-3 rounded-xl border border-white/10">
              {inventory.map(item => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => toggleInventory(item.id!)}
                  className={`flex flex-col items-center justify-center p-2 rounded-lg border transition-all ${
                    formData.inventoryItemIds?.includes(item.id!)
                      ? 'bg-lcars-blue border-lcars-blue text-black'
                      : 'bg-white/5 border-white/10 text-[var(--foreground)]/60 hover:border-lcars-blue/50'
                  }`}
                >
                  <Box className="w-5 h-5 mb-1" />
                  <span className="text-[0.6rem] font-bold uppercase text-center line-clamp-1">{item.name}</span>
                </button>
              ))}
              {inventory.length === 0 && (
                <div className="col-span-full py-4 text-center text-[var(--foreground)]/20 text-[0.6rem] uppercase tracking-widest font-bold">
                  No assets found in primary registry
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => { setIsAdding(false); setEditingId(null); }} className="lcars-pill bg-lcars-red text-[var(--foreground)] px-4 py-2 text-xs font-bold uppercase">Abort</button>
            <button type="submit" className="lcars-pill bg-lcars-blue text-black px-4 py-2 text-xs font-bold uppercase">Activate Config</button>
          </div>
        </form>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {props.map(prop => (
          <div key={prop.id} className="bg-[var(--background)] border-l-8 border-lcars-purple p-5 hover:bg-white/5 transition-all group relative">
            <div className="flex justify-between items-start mb-3">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge className="bg-lcars-purple text-black text-[0.7rem] font-bold px-3 py-0.5">
                  {prop.staffNeeded} SHIFT CREW NEEDED
                </Badge>
                {(prop.setupStaffNeeded !== undefined) && (
                  <Badge className="bg-lcars-blue text-black text-[0.7rem] font-bold px-3 py-0.5">
                    {prop.setupStaffNeeded} SETUP CREW
                  </Badge>
                )}
                {(prop.takedownStaffNeeded !== undefined) && (
                  <Badge className="bg-lcars-red text-black text-[0.7rem] font-bold px-3 py-0.5">
                    {prop.takedownStaffNeeded} TAKEDOWN CREW
                  </Badge>
                )}
              </div>
              <div className="flex gap-1 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity">
                <button onClick={() => { setEditingId(prop.id!); setFormData(prop); }} className="p-1 hover:text-lcars-blue text-[var(--foreground)]/50"><Edit2 className="w-4 h-4" /></button>
                <button onClick={() => handleDelete(prop.id!)} className="p-1 hover:text-lcars-red text-[var(--foreground)]/50"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
            
            <h3 className="text-xl font-bold text-[var(--foreground)] mb-1 flex items-center gap-3">
              <Layout className="w-5 h-5 text-lcars-tan" />
              {prop.name}
            </h3>
            <p className="text-sm text-[var(--foreground)]/60 mb-4">{prop.description || 'No operational baseline provided.'}</p>
            
            <div className="space-y-2">
              <div className="text-[0.6rem] font-bold text-lcars-purple uppercase tracking-widest border-b border-lcars-purple/30 pb-1 mb-2">Required Equipment</div>
              <div className="flex flex-wrap gap-2">
                {prop.inventoryItemIds?.map(itemId => {
                  const item = inventory.find(i => i.id === itemId);
                  return (
                    <div key={itemId} className="flex items-center gap-1.5 bg-white/5 px-2 py-1 rounded text-[0.65rem] font-bold text-lcars-tan border border-lcars-tan/20">
                      <ShieldCheck className="w-3 h-3" />
                      {item?.name || 'Unknown Asset'}
                    </div>
                  );
                })}
                {(!prop.inventoryItemIds || prop.inventoryItemIds.length === 0) && (
                  <span className="text-[0.6rem] text-[var(--foreground)]/20 italic font-bold">No physical assets assigned</span>
                )}
              </div>
            </div>
          </div>
        ))}
        {props.length === 0 && !loading && (
          <div className="col-span-full py-12 text-center bg-white/5 border-2 border-dashed border-white/10 rounded-lg">
             <Layout className="w-12 h-12 mx-auto mb-2 text-[var(--foreground)]/20" />
             <p className="text-[var(--foreground)]/40 font-bold uppercase tracking-widest">No prop configurations registered in tactical database.</p>
          </div>
        )}
      </div>

      <LCARSConfirmationModal
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        description={confirmDialog.description}
      />
    </div>
  );
}
